import { wrap180, wrap360 } from './math';

export interface CircularLowPassOptions {
  /** Normal time constant (s). */
  tauS: number;
  /** Time constant while in fast mode (s). */
  fastTauS: number;
  /** Fast mode for this long after the first sample (s). */
  initialFastS: number;
  /** Enter fast mode when the (smoothed) innovation stays above this (deg) ... */
  innovationDeg: number;
  /** ... for longer than this (s). */
  innovationHoldS: number;
  /** Leave fast mode when the smoothed innovation falls below this (deg). */
  exitDeg: number;
  /** Time constant (s) of the innovation average used for the fast-mode decisions. */
  innovationTauS: number;
}

export const DEFAULT_CIRCULAR_OPTIONS: CircularLowPassOptions = {
  tauS: 5,
  fastTauS: 0.5,
  initialFastS: 2,
  innovationDeg: 10,
  innovationHoldS: 3,
  exitDeg: 3,
  innovationTauS: 1,
};

/**
 * Adaptive first-order low-pass for angles in degrees (wraps at 0/360).
 *
 * tau = 5 s normally; fast (0.5 s) for the first 2 s, while the mean
 * innovation has stayed > 10 deg for > 3 s (until it drops below 3 deg), and
 * during an explicit boost() (e.g. after an XR tracking reset).
 */
export class CircularLowPass {
  readonly opts: CircularLowPassOptions;
  /** Filtered angle in [0, 360), NaN before the first sample. */
  value = NaN;
  initialized = false;
  /** Signed mean innovation (deg). */
  meanInnovation = 0;
  fast = false;
  private age = 0;
  private highTime = 0;
  private boostLeft = 0;

  constructor(opts: Partial<CircularLowPassOptions> = {}) {
    this.opts = { ...DEFAULT_CIRCULAR_OPTIONS, ...opts };
  }

  reset(): void {
    this.value = NaN;
    this.initialized = false;
    this.meanInnovation = 0;
    this.fast = false;
    this.age = 0;
    this.highTime = 0;
    this.boostLeft = 0;
  }

  /** Use the fast time constant for the next `seconds`. */
  boost(seconds: number): void {
    if (seconds > this.boostLeft) this.boostLeft = seconds;
  }

  /** True while the fast time constant is in use. */
  isFast(): boolean {
    return this.age < this.opts.initialFastS || this.fast || this.boostLeft > 0;
  }

  /** Feed a sample (deg) observed `dtS` seconds after the previous one. */
  update(sampleDeg: number, dtS: number): number {
    if (!Number.isFinite(sampleDeg)) return this.value;
    if (!this.initialized) {
      this.value = wrap360(sampleDeg);
      this.initialized = true;
      this.age = 0;
      this.meanInnovation = 0;
      this.highTime = 0;
      this.fast = false;
      return this.value;
    }
    const dt = dtS > 0 ? dtS : 0;
    const o = this.opts;
    const innov = wrap180(sampleDeg - this.value);
    this.meanInnovation += (innov - this.meanInnovation) * (1 - Math.exp(-dt / o.innovationTauS));
    const absMean = Math.abs(this.meanInnovation);
    if (absMean > o.innovationDeg) this.highTime += dt;
    else this.highTime = 0;
    if (!this.fast && this.highTime > o.innovationHoldS) this.fast = true;
    else if (this.fast && absMean < o.exitDeg) this.fast = false;

    const tau = this.isFast() ? o.fastTauS : o.tauS;
    this.value = wrap360(this.value + innov * (1 - Math.exp(-dt / tau)));
    this.age += dt;
    if (this.boostLeft > 0) this.boostLeft = Math.max(0, this.boostLeft - dt);
    return this.value;
  }
}

/** Scalar first-order low-pass with a time constant; first sample initialises. */
export class LowPass {
  value = NaN;
  initialized = false;
  tauS: number;
  constructor(tauS: number) {
    this.tauS = tauS;
  }

  reset(): void {
    this.value = NaN;
    this.initialized = false;
  }

  update(sample: number, dtS: number): number {
    if (!Number.isFinite(sample)) return this.value;
    if (!this.initialized) {
      this.value = sample;
      this.initialized = true;
      return sample;
    }
    const dt = dtS > 0 ? dtS : 0;
    this.value += (sample - this.value) * (1 - Math.exp(-dt / this.tauS));
    return this.value;
  }
}
